import { Component, EventEmitter, OnInit, Output, inject, signal, computed, effect, Input, OnDestroy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { JobFormData, Job, Phase, Equipment } from '@shared/model/job-type-data.model';
import { EmployeeService } from '@pages/employee/services/employee.service';
import { HomeService } from '@pages/home/services/home.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';

@Component({
  selector: 'app-job-type-dropdown',
  templateUrl: './job-type-dropdown.component.html',
  styleUrls: ['./job-type-dropdown.component.css'],
})
export class JobTypeDropdownComponent implements OnInit, OnDestroy {
  @Input() inputJob: Job | null = null;
  @Input() inputPhase: Phase | null = null;
  @Input() inputEquipment: Equipment | null = null;
  @Input() employeeInfo: any;
  @Output() dropdownChanged = new EventEmitter<JobFormData>();

  private readonly employeeService = inject(EmployeeService);
  private readonly homeService = inject(HomeService);
  private readonly portalSettingsService = inject(PortalSettingsService);

  private readonly portalSettings = this.portalSettingsService.getPortalSettings();

  private readonly noPhase = { JCJPKeyID: 0, Phase: '', PhaseDescription: 'No Phase', PhaseGroup: 0 } as const satisfies Phase;

  equipmentInput$ = new Subject<string>();

  equipment$ = this.equipmentInput$.pipe(
    debounceTime(500),
    distinctUntilChanged(),
    switchMap(term =>
      term?.length > 2
        ? this.employeeService
            .getEquipments(term, null, null, null, null)
            .pipe(
              map((response: any) =>
                response.map((eq: any) => ({
                  ...eq,
                  DisplayValue: `${eq.EMCo} - ${eq.Equipment} - ${eq.Description}`,
                } as Equipment))
              )
            )
        : of([])
    )
  );

  jobList = signal<Job[]>([]);
  phaseList = signal<Phase[]>([]);
  equipmentList = signal<Equipment[]>([]);

  selectedJob = signal<Job | null>(null);
  selectedPhase = signal<Phase | null>(null);
  selectedEquipment = signal<Equipment | null>(null);

  prefillJP = computed(() => this.portalSettings?.PrefillPerviousJobPhase?.ConfigValue === '1');
  tcClockIOIgnoreJobPhase = computed(() => this.portalSettings?.TCClockIOIgnoreJobPhase?.ConfigValue === '1');
  enableEquipmentLookup = computed(() => this.portalSettings?.ClockInAddEquipment?.ConfigValue === '1');

  isValidForm = computed(() => {
    const hasJobAndPhase =
      (!this.tcClockIOIgnoreJobPhase() && this.selectedJob()?.JCJMKeyID != null && this.selectedPhase()?.JCJPKeyID != null) ||
      (this.tcClockIOIgnoreJobPhase() && this.selectedJob() != null && this.selectedPhase() != null);

    return hasJobAndPhase;
  });

  selectedJobTypeValues = computed<JobFormData>(() => ({
    selectedJob: this.selectedJob(),
    selectedPhase: this.selectedPhase(),
    selectedEquipment: this.selectedEquipment(),
    isValidForm: this.isValidForm(),
  }));

  constructor() {
    effect(() => {
      this.dropdownChanged.emit(this.selectedJobTypeValues());
    });
  }

  ngOnInit(): void {
    this.getJobs();

    if (this.enableEquipmentLookup()) {
      this.equipment$.subscribe(equipment => this.equipmentList.set(equipment));
    }

    if (this.inputJob) {
      this.selectedJob.set(this.inputJob);
      this.getPhases();
    }
    if (this.inputPhase) {
      this.selectedPhase.set(this.inputPhase);
    }
    if (this.inputEquipment) {
      this.selectedEquipment.set(this.inputEquipment);
    }
  }

  ngOnDestroy(): void {
    this.equipmentInput$.unsubscribe();
  }

  async getJobs(): Promise<void> {
    const response = await this.homeService.getJobs('TC');
    if (response != null) {
      for (let i = 0; i < response.length; i++) {
        response[i].DisplayValue = 'JCCo ' + response[i].JCCo + ' - ' + response[i].JobDisplayName;
        
        // Prefill logic - compare numbers (convert bigint to number if needed)
        if (
          this.employeeInfo.LastUsedJCJMKeyID != null &&
          response[i].JCJMKeyID == Number(this.employeeInfo.LastUsedJCJMKeyID) &&
          this.prefillJP()
        ) {
          this.selectedJob.set(response[i] as Job);
        }
      }

      // Add "No Job" option if configured
      if (this.tcClockIOIgnoreJobPhase()) {
        response.unshift({
          JCJMKeyID: undefined,
          DisplayValue: 'No Job',
          JCCo: undefined,
          Job: '',
          JobDescription: '',
          JobDisplayName: '',
        });
        if (this.selectedJob() == null) {
          this.selectedJob.set(response[0] as Job);
        }
      }

      this.jobList.set(response as Job[]);

      // Auto-load phases if job is prefilled
      if (this.selectedJob() != null) {
        await this.getPhases();
      } else {
        // Add "No Phase" option
        this.phaseList.set([this.noPhase]);
        this.selectedPhase.set(this.phaseList()[0]);
      }
    }
  }

  async getPhases(): Promise<void> {
    this.phaseList.set([]);
    const selectedJob = this.selectedJob();

    if (selectedJob?.JCJMKeyID != null) {
      const response = await this.employeeService.getPhasesByJCJMKeyID(selectedJob.JCJMKeyID, true);

      if (response != null) {
        const phases: Phase[] = response as Phase[];

        // Prefill logic - compare numbers (convert bigint to number if needed)
        if (this.prefillJP()) {
          for (let i = 0; i < phases.length; i++) {
            if (
              this.employeeInfo.LastUsedJCJPKeyID != null &&
              Number(this.employeeInfo.LastUsedJCJPKeyID) == phases[i].JCJPKeyID
            ) {
              this.selectedPhase.set(phases[i]);
            }
          }
        }

        // Add "No Phase" option if configured
        if (this.tcClockIOIgnoreJobPhase()) {
          phases.unshift(this.noPhase);
          if (this.selectedPhase() == null) {
            this.selectedPhase.set(phases[0]);
          }
        }

        this.phaseList.set(phases);
      }
    } else {
      // No job selected, add "No Phase" option
      if (this.tcClockIOIgnoreJobPhase()) {
        this.phaseList.set([this.noPhase]);
        if (this.selectedPhase() == null) {
          this.selectedPhase.set(this.phaseList()[0]);
        }
      }
    }
  }

  jobChanged(job: Job): void {
    this.selectedJob.set(job);
    this.selectedPhase.set(null);

    if (job?.JCJMKeyID != null) {
      this.homeService.setJob(job.JCJMKeyID, this.employeeInfo.UserKeyID);
      this.getPhases();
    } else {
      this.phaseList.set([this.noPhase]);
      this.selectedPhase.set(this.phaseList()[0]);
    }
  }

  phaseChanged(phase: Phase): void {
    this.selectedPhase.set(phase);
  }

  equipmentChanged(equipment: Equipment): void {
    this.selectedEquipment.set(equipment);
  }

  searchJobs(term: string, item: Job): boolean {
    term = term.trim().toLowerCase();
    return item.JCJMKeyID?.toString().indexOf(term) > -1 || item.DisplayValue?.toLowerCase().indexOf(term) > -1;
  }

  searchPhases(term: string, item: Phase): boolean {
    term = term.trim().toLowerCase();
    return (
      item.JCJPKeyID?.toString().indexOf(term) > -1 ||
      item.PhaseDescription?.toLowerCase().indexOf(term) > -1 ||
      item.PhaseGroup?.toString().indexOf(term) > -1
    );
  }
}
